import { useCallback, useMemo } from "react";
import SidePeekIcon from "@/assets/icons/SidePeekIcon";
import { Cell, Row, Table } from "@tanstack/react-table";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { ListType } from "@/types/List.type";
import { TableMetaProps } from "@/hooks/table/useTableConfig";
import useEditableCell from "@/hooks/useEditableCell";
import Button from "@/components/atoms/Button";
import Checkbox from "@/components/atoms/Checkbox";
import { ContextMenu, ContextMenuTrigger } from "@/components/atoms/ContextMenu";
import CellContent from "@/components/molecules/table/CellContent";
import TableRowContextMenuCompany from "@/components/molecules/table/context/TableRowContextMenuCompany";
import TableRowContextMenuLists from "@/components/molecules/table/context/TableRowContextMenuLists";
import { TableType } from "@/components/organisms/table/Table";

type TableCellProps<T> = {
  table: Table<T>;
  cell: Cell<T, unknown>;
  index: number;
  row: Row<T>;
  tableType: TableType;
  isLastColumn: boolean;
  isLastRow: boolean;
  canReorderRows: boolean;
  canEdit?: boolean;
  onRename?: (id: string, newValue: string) => void;
  hasContextMenu?: boolean;
  onRemoveRows: (rowIds: string[]) => void;
  canSelectRows?: boolean;
  isDraggedOver?: boolean;
  onDetailClicked?: (rowId: string) => void;
  activeDetailId: string | null;
};

const TableCell = <T,>({
  table,
  cell,
  index,
  row,
  tableType = TableType.COMPANIES,
  isLastColumn,
  //canReorderRows = false,
  canEdit = false,
  onRename,
  hasContextMenu = false,
  onRemoveRows,
  canSelectRows,
  isDraggedOver,
  onDetailClicked,
  activeDetailId,
}: TableCellProps<T>) => {
  const { t } = useTranslation();
  const style = {
    // Fixed to 300px to prevent jumping columns with virtualization
    minWidth: "300px", //`${cell.column.getSize()}px`,
  };

  const rowId = row.id;
  const originalValue = cell.getValue<never>();

  const onExitEditMode = (id: string, newValue?: string) => {
    if (newValue && onRename) {
      onRename(id, newValue);
    }
  };

  const editableCell = useEditableCell({
    originalValue,
    onExitEditMode,
    rowId,
  });

  const meta: TableMetaProps = cell.column.columnDef.meta || {};

  // Determine the sortable ID based on handle type
  /*const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `row-${row.id}`,
    disabled: !canReorderRows,
  });*/

  const selectedRows = useMemo(() => table.getSelectedRowModel().rows, [table.getSelectedRowModel().rows]);
  const triggeredRows = useMemo(() => (selectedRows.includes(row) ? selectedRows : [row]), [row, selectedRows]);

  const handleDetailClicked = useCallback(() => onDetailClicked?.(rowId), [onDetailClicked, rowId]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <td
            style={style}
            className={clsx(
              "relative w-full flex-1 border-b border-r border-secondary pl-4 pr-1.5 text-left text-sm text-quaternary",
              index === 0 && "sticky left-0 z-30 bg-white font-medium !text-primary hover:z-[31]",
              row.getIsSelected() && "!bg-brand-primary-subtle hover:!bg-brand-primary",
              !row.getIsSelected() && "hover:bg-gray-100 group-hover:bg-gray-50",
              isLastColumn && "border-r-0",
              isDraggedOver && "border-r-4 border-r-brand-primary",
              activeDetailId === rowId && "!bg-brand-primary hover:!bg-brand-primary",
              meta.column?.className,
            )}
          >
            <div className={"relative flex h-full items-center truncate"}>
              {index === 0 && canSelectRows && (
                <div className={"h-4 w-6 shrink-0"}>
                  <Checkbox
                    {...{
                      checked: row.getIsSelected(),
                      onChange: row.getToggleSelectedHandler(),
                      className: row.getIsSelected() || row.getIsSomeSelected() ? "block" : "hidden group-hover:block",
                    }}
                  />
                </div>
              )}
              <CellContent
                cell={cell}
                editableCell={editableCell}
                index={index}
                onDetailClicked={handleDetailClicked}
              />

              {onDetailClicked && index === 0 && (
                <>
                  <div className={"absolute right-0 hidden group-hover:block"}>
                    <Button
                      onClick={handleDetailClicked}
                      label={t("company.detail.details")}
                      leadingIcon={<SidePeekIcon className={"size-4 text-fg-tertiary"} />}
                      color={"natural"}
                      variants={"outline"}
                      size={"2xs"}
                      className={"!px-2 !py-1"}
                    />
                  </div>
                </>
              )}
            </div>
          </td>
        </ContextMenuTrigger>
        {hasContextMenu && index === 0 ? (
          tableType === TableType.COMPANIES ? (
            <TableRowContextMenuCompany selectedRows={triggeredRows} onRemoveRows={onRemoveRows} />
          ) : (
            <TableRowContextMenuLists
              selectedRows={triggeredRows as unknown as Row<ListType>[]}
              onRemoveRows={onRemoveRows}
              onRename={() => canEdit && editableCell.enterEditMode()}
            />
          )
        ) : null}
      </ContextMenu>
    </>
  );
};

export default TableCell;
